import { Component, ElementRef, OnDestroy, OnInit, ViewChild, Input, AfterViewInit, TemplateRef } from '@angular/core';
import { Store } from '../../../store';
import { CallLogsSearchParams, defaultCallLogsSearchParams } from '../../../data-types/search-params';
import { TwilioService } from '../../../services/twilio.service';
import { AlertService } from '../../../services/alert.service';
import { bufferCount, first, takeUntil } from 'rxjs/operators';
import { animate, state, style, transition, trigger } from '@angular/animations';
import { UserAccountType } from '../../../data-types/user-data';
import { Subject, Subscription } from 'rxjs';
import { BsDatepickerConfig } from 'ngx-bootstrap/datepicker';
import { AlertConfirmable } from '../../../decorators/alert-confirmable.decorator';
import { debounce } from 'lodash';
import { Socket } from 'ngx-socket-io';
import { environment as env } from '../../../../environments/environment';
import { CustomAlertData, DefaultCustomAlertData } from '../../../data-types/alert-data';
import { Notes } from '../../../models/notes';
import { ToolsService } from '../../../services/tools.service';

@Component({
    selector: 'app-calls-history',
    templateUrl: './calls-history.component.html',
    styleUrls: ['./calls-history.component.scss'],
    animations: [
        trigger('fadeIn', [
            state('void', style({ opacity: '0' })),
            transition('void <=> *', [animate('150ms ease-in')]),
        ]),
        trigger('fadeOut', [
            state('*', style({ opacity: '1' })),
            transition('* <=> void', [animate('100ms ease-out')]),
        ]),
        trigger('fadeInOut', [
            state('void', style({ opacity: '0' })),
            transition('void => *', [animate('150ms ease-out')]),
            state('*', style({ opacity: '1' })),
            transition('* => void', [animate('50ms ease-in')]),
        ]),
    ],
})
export class CallsHistoryComponent implements OnInit, OnDestroy, AfterViewInit {
    @Input() onlyTable = false;
    @Input() phoneNr: Array<string> = null;

    @ViewChild('notesTmpl', { read: TemplateRef }) notesTmpl: TemplateRef<any>;

    private voiceSocket: Socket; // here comes all sorts of messages from CRM
    isLoading = false;
    env = env;

    bsConfig?: Partial<BsDatepickerConfig> = Object.assign(
        {},
        {
            containerClass: 'theme-default test',
            isAnimated: true,
            dateInputFormat: 'DD/MM/YYYY',
            keepDatepickerOpened: true,
        }
    );

    accountTypes: Array<{ name: string; value: string }> = [];

    @ViewChild('dropDownSelect') dropDownSelect: ElementRef<HTMLElement>;

    logSwitchItems = [
        { switchName: 'Mine', isSwitchActive: true, id: 1 },
        { switchName: 'All', isSwitchActive: false, id: 2 },
    ];

    callHistory: Array<any> = [];
    logParams: CallLogsSearchParams = { ...defaultCallLogsSearchParams };

    sound = {};
    playing = null;
    audio = null;
    callNotes: Notes[] | null = null;

    destroy$ = new Subject<void>();

    currentPhone;
    user = this.store.selectForLocal('user');

    callHistoryListDebounce;
    private callHistoryListCancel$ = new Subject<Subscription>();

    constructor(
        private store: Store,
        public twilioService: TwilioService,
        private alertService: AlertService,
        public toolsService: ToolsService,
    ) {
        this.accountTypes = Object.keys(UserAccountType).map((key) => ({
            value: UserAccountType[key],
            name: key,
        }));

        this.callHistoryListDebounce = debounce(this.callHistoryList, 350);
        this.callHistoryListCancel$.pipe(bufferCount(2, 1)).subscribe(([oldSub, newSub]) => oldSub.unsubscribe());
    }

    ngAfterViewInit(): void {
        this.currentPhone = this.phoneNr || this.twilioService.numbersForDomain.map(item => item.phone);

        if (this.currentPhone) {
            this.logParams = {
                ...defaultCallLogsSearchParams,
                phoneNr: this.currentPhone,
            };
            this.callHistoryList();

            if (this.voiceSocket) {
                this.voiceSocket.disconnect();
            }
            this.voiceSocket = new Socket({ url: `${env.socketPath}voice`, options: { withCredentials: true } });
            this.voiceSocket.fromEvent('log-row').subscribe((logRow: { callSid: string }) => this.callHistoryList());
        }
    }

    ngOnInit(): void {}

    @AlertConfirmable({ title: 'Call to the number', message: 'Do you want to make a call?' })
    makeACallLog(callObjet: any) {
        if (!this.twilioService.isLineBusy) {
            this.twilioService.startOutboundCall(callObjet.direction === 'out' ? callObjet.callTo : callObjet.callFrom);
        } else {
            this.alertService.error([
                'Line is busy, please nicely finish ongoing conversation before starting the new one.',
            ]);
        }
    }

    callHistoryList(param?: { [property in keyof CallLogsSearchParams]?: CallLogsSearchParams[property] }) {
        this.isLoading = true;
        if (param) {
            this.logParams = { ...this.logParams, page: 0, ...param } as CallLogsSearchParams;
        }

        this.callHistoryListCancel$.next(
            this.twilioService
                .getCallHistoryForNr(
                    this.logParams.page * this.logParams.limit,
                    this.logParams.limit,
                    {
                        dir: this.logParams.direction,
                        accType: this.logParams.accountType,
                        searchWord: this.logParams.name,
                        date: this.logParams.date,
                        sort: this.logParams.sort,
                        ascOrDesc: this.logParams.ascOrDesc,
                    },
                    this.logParams.phoneNr
                )
                .pipe(takeUntil(this.destroy$), first())
                .subscribe((result: { list: Array<any>; totalRecords: number }) => {
                    this.callHistory = [];
                    if (result.list.length > 0) {
                        this.callHistory = result.list;
                    }

                    if (result.totalRecords) {
                        this.logParams.pages = Math.ceil(result.totalRecords / this.logParams.limit);
                        this.logParams.totalRecords = result.totalRecords;
                        this.logParams.pageArray = [];
                        for (let i = 0; i < this.logParams.pages; i++) {
                            this.logParams.pageArray.push(i);
                        }
                    }

                    this.isLoading = false;
                })
        );
    }

    showNotesFor(callLog) {
        this.callNotes = null;
        this.twilioService.getNotes(callLog.sId).subscribe(notes => this.callNotes = notes);
        this.alertService
            .customAlert({
                    ...DefaultCustomAlertData,
                    template: this.notesTmpl,
                    cancelText: null,
                    confirmText: 'Close'
                } as CustomAlertData
            );
    }

    ngOnDestroy(): void {
        if (this.audio) {
            this.audio.pause();
            document.removeChild(this.audio);
            this.audio = null;
        }
        this.destroy$.next();
        if (this.voiceSocket) {
            this.voiceSocket.disconnect();
        }
    }

    get UserAccountTypeEnum() {
        return Object.values(UserAccountType);
    }
}
